<template>
	<div class="group flex gap-y-2">
		<div
			class="grow px-4 py-2 print:py-1 align-top bg-base cursor-pointer transition-colors group-hover:bg-base-100 rounded-tl-lg rounded-bl-lg"
			@click="onLessonPlanClicked(lessonPlan)"
		>
			<div class="flex flex-col mb-1">
				<div class="flex items-center gap-2.5">
					<div class="text-sm">
						{{ lessonPlan.title || "" }}
					</div>
					<LessonPlanCollectionsSelector
						v-if="withCollectionsSelector"
						:lessonPlan="lessonPlan"
						:align="'start'"
						:side="'right'"
					>
						<template #trigger="{ toggle }">
							<div
								v-if="lessonPlan.isSavedInLib"
								class="shrink-0 hover:cursor-pointer relative group/collection-button transition-all duration-300 tooltip"
								:data-tip="$t('Saved in library')"
							>
								<button
									type="button"
									@click.stop="toggle"
									class="btn btn-circle btn-xs bg-indigo-200 border-none group-hover/collection-button:scale-105 group-hover/collection-button:bg-indigo-200"
								>
									<RectangleStackIcon class="w-4 h-4 text-indigo-500" />
								</button>
							</div>
							<div
								v-else
								class="relative shrink-0 hover:cursor-pointer group/collection-button transition-all duration-300 tooltip"
								:data-tip="$t('Not saved in library')"
							>
								<button
									type="button"
									@click.stop="toggle"
									class="btn btn-circle btn-xs bg-base-200 relative border-none group-hover/collection-button:scale-105 group-hover/collection-button:bg-base-200"
								>
									<RectangleStackIcon class="w-4 h-4 text-base-content" />
									<PlusIcon
										class="w-3 h-3 absolute top-0 -right-1.5 text-base-content bg-primary rounded-full"
									/>
								</button>
							</div>
						</template>
					</LessonPlanCollectionsSelector>
				</div>

				<div v-if="showCreatorName" class="flex gap-2">
					<div class="flex items-center gap-2 text-xs text-accent-focus bg-accent-focus/10 py-0.5 px-2 mt-0.5 max-w-max rounded">
						<!-- Phosphor icon PenNib -->
						<svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" fill="currentColor" viewBox="0 0 256 256">
							<path d="M248,92.68a15.86,15.86,0,0,0-4.69-11.31L174.63,12.68a16,16,0,0,0-22.63,0L123.57,41.11l-58,21.77A16.06,16.06,0,0,0,55.35,75.23L32.11,214.68A8,8,0,0,0,40,224a8.4,8.4,0,0,0,1.32-.11l139.44-23.24a16,16,0,0,0,12.35-10.17l21.77-58L243.31,104A15.87,15.87,0,0,0,248,92.68Zm-69.87,92.19L63.32,204l47.37-47.37a28,28,0,1,0-11.32-11.32L52,192.7,71.13,77.86,126,57.29,198.7,130ZM112,132a12,12,0,1,1,12,12A12,12,0,0,1,112,132Zm96-15.32L139.31,48l24-24L232,92.68Z"></path>
						</svg>
						<p>{{ lessonPlan.creatorName }}</p>
						<div v-if="lessonPlan.coCreatorName" class="flex items-center gap-2">
							<span>/</span><span>{{ lessonPlan.coCreatorName }}</span>
						</div>
					</div>
				</div>

			</div>
			<div v-if="lessonPlan.instructions" class="text-xs text-base-content-light line-clamp-3 break-words">
				{{ extractText(lessonPlan.instructions) }}
			</div>
			<div class="flex items-center justify-start mt-1 gap-2">

				<!-- Supplies badge -->
				<HoverCardRoot v-if="lessonPlan.supplies?.length" :open-delay="600" :open="openCard === 'supplies'" @update:open="val => openCard = val ? 'supplies' : null">
					<HoverCardTrigger>
						<span class="badge text-xs border-0 text-base-content/50 group-hover:bg-base">
							<!-- pencil-ruler icon from phosphoricons -->
							<svg class="w-3 h-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 256 256">
								<path d="M208,34H160a14,14,0,0,0-14,14V208a14,14,0,0,0,14,14h48a14,14,0,0,0,14-14V48A14,14,0,0,0,208,34Zm2,174a2,2,0,0,1-2,2H160a2,2,0,0,1-2-2V174h26a6,6,0,0,0,0-12H158V134h26a6,6,0,0,0,0-12H158V94h26a6,6,0,0,0,0-12H158V48a2,2,0,0,1,2-2h48a2,2,0,0,1,2,2ZM76.24,27.76a6,6,0,0,0-8.48,0l-32,32A6,6,0,0,0,34,64V208a14,14,0,0,0,14,14H96a14,14,0,0,0,14-14V64a6,6,0,0,0-1.76-4.24ZM46,178V78H66V178ZM78,78H98V178H78ZM72,40.49,97.51,66h-51ZM96,210H48a2,2,0,0,1-2-2V190H98v18A2,2,0,0,1,96,210Z"></path>
							</svg>
							{{ lessonPlan.supplies.length }}
						</span>
					</HoverCardTrigger>
					<HoverCardPortal>
						<HoverCardContent class="HoverCardContent w-80 border border-base-200 z-10" align="center">
							<h1>{{ $t('Supplies') }}</h1>
							<ul class="list-disc pl-5 space-y-1">
								<li v-for="(supply, index) in lessonPlan.supplies" :key="index">
									<span class="text-sm">{{ supply }}</span>
								</li>
							</ul>
						</HoverCardContent>
					</HoverCardPortal>
				</HoverCardRoot>

				<!-- Goals badges: one per curriculum -->
				<HoverCardRoot
					v-for="group in goalGroups"
					:key="group.key"
					:open-delay="600"
					:open="openCard === 'goals-' + group.key"
					@update:open="val => openCard = val ? 'goals-' + group.key : null"
				>
					<HoverCardTrigger>
						<span class="badge text-xs border-0 text-base-content/50 group-hover:bg-base">
							<MapPinIcon class="w-3 h-3 mr-1"></MapPinIcon>
							{{ goalBadgeLabel(group) }}
						</span>
					</HoverCardTrigger>
					<HoverCardPortal>
						<HoverCardContent class="HoverCardContent w-96 border border-base-200 z-10">
							<h1>{{ goalGroups.length > 1 ? group.curriculum.name : $tc('Goal', 2) }}</h1>
							<ul class="flex flex-col gap-2">
								<li
									v-for="goal in group.goals"
									:key="goal.id"
									class="flex items-start gap-2"
									:class="[goal.isSubgoal() ? 'pl-6 text-sm' : '']"
								>
									<MapPinIcon
										class="shrink-0 mt-0.5 w-4 h-4"
										:class="{ color: goal.color ?? 'bg-base-300' }"
									></MapPinIcon>
									<span class="text-sm text-nowrap">{{ goal.fullPath }}</span>
									<span class="truncate text-sm">
										<GoalTitle :goal="goal" :short="true" class="text-sm" />
									</span>
								</li>
							</ul>
						</HoverCardContent>
					</HoverCardPortal>
				</HoverCardRoot>

				<!-- Attachments badge -->
				<HoverCardRoot v-if="lessonPlan.attachments?.length" :open-delay="600" :open="openCard === 'attachments'" @update:open="val => openCard = val ? 'attachments' : null">
					<HoverCardTrigger>
						<span class="badge text-xs border-0 text-base-content/50 group-hover:bg-base">
							<PaperClipIcon class="w-3 h-3 mr-1"></PaperClipIcon>
							{{ lessonPlan.attachments.length }}
						</span>
					</HoverCardTrigger>
					<HoverCardPortal>
						<HoverCardContent class="HoverCardContent w-80 border border-base-200 z-10">
							<h1>{{ $t('Attachments') }}</h1>
							<ul class="flex flex-col gap-1">
								<li v-for="file in sortedAttachments(lessonPlan)" :key="file.id">
									<AttachmentsUploadedFile :file="file" :readOnly="true" class="text-sm" />
								</li>
							</ul>
						</HoverCardContent>
					</HoverCardPortal>
				</HoverCardRoot>

				<!-- Collections badge -->
				<HoverCardRoot v-if="lessonPlan.collectionIds?.length" :open-delay="600" :open="openCard === 'collections'" @update:open="val => openCard = val ? 'collections' : null">
					<HoverCardTrigger>
						<span class="badge text-xs border-0 text-base-content/50 group-hover:bg-base">
							<ListBulletIcon class="w-3 h-3 mr-1"></ListBulletIcon>
							{{ lessonPlan.collectionIds.length }}
						</span>
					</HoverCardTrigger>
					<HoverCardPortal>
						<HoverCardContent class="HoverCardContent w-80 border border-base-200 z-10">
							<h1>{{ $t('Collections') }}</h1>
							<div class="space-y-1">
								<LessonPlanCollectionsListItem
									v-for="collection in collections"
									:collection="collection"
									:key="collection.id"
									:showMarker="true"
									class="pointer-events-none"
								/>
							</div>
						</HoverCardContent>
					</HoverCardPortal>
				</HoverCardRoot>
			</div>
		</div>

		<div
			class="shrink-0 px-4 py-3 align-top whitespace-nowrap text-right bg-base cursor-pointer transition-colors group-hover:bg-base-100 rounded-tr-lg rounded-br-lg flex items-center justify-end"
			@click="onLessonPlanClicked(lessonPlan)"
		>
			<ChevronRightIcon
				class="w-5 h-5 text-base-content/40 transition-colors group-hover:text-neutral-focus"
			/>
		</div>
	</div>
</template>


<script lang="ts">

import { PropType } from "vue";
import { mapStores } from "pinia";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import { LessonPlan } from "@/models/LessonPlan.model";
import { Curriculum, CurriculumGoalGroup, countParentGoals } from "@/models/Curriculum.model";
import { Goal } from "@/models/Goal.model";
import { MapPinIcon, PaperClipIcon, ListBulletIcon, ChevronRightIcon, RectangleStackIcon, PlusIcon } from "@heroicons/vue/24/outline";
import GoalTitle from "@/components/GoalTitle.vue";
import { HoverCardArrow, HoverCardContent, HoverCardPortal, HoverCardRoot, HoverCardTrigger } from 'reka-ui';
import AttachmentsUploadedFile from "@/components/AttachmentsUploadedFile.vue";
import { tiptapUtils } from "@/utils/TipTap.ts";
import LessonPlanCollectionsListItem from "@/components/LessonPlanCollectionsListItem.vue";
import LessonPlanCollectionsSelector from '@/components/LessonPlanCollectionsSelector.vue';


export default {
	name: "LessonPlanListItem",

	emits: [
		'lessonPlanClicked',
	],

	props: {
		lessonPlan: {
			type: Object as PropType<LessonPlan>,
			required: true,
		},
		withCollectionsSelector: {
			type: Boolean,
			default: false,
		},
		collections: {
			type: Array,
			default: null,
		},
		showCreatorName: {
			type: Boolean,
			default: false,
		},
	},

	components: {
		MapPinIcon, PaperClipIcon, ListBulletIcon, ChevronRightIcon, RectangleStackIcon, PlusIcon,
		GoalTitle,
		HoverCardTrigger, HoverCardRoot, HoverCardPortal, HoverCardContent, HoverCardArrow,
		AttachmentsUploadedFile,
		LessonPlanCollectionsListItem,
		LessonPlanCollectionsSelector,
	},

	mounted() {
		// Add a scroll listener to close hover cards when scrolling the parent container to avoid
		// cards covering surrounding content when the user scrolls.
		const scrollContainer = this.$el.closest('.overflow-y-auto');
		if (scrollContainer) {
			scrollContainer.addEventListener('scroll', this.closeHoverCards);
		}
	},

	beforeUnmount() {
		const scrollContainer = this.$el.closest('.overflow-y-auto');
		if (scrollContainer) {
			scrollContainer.removeEventListener('scroll', this.closeHoverCards);
		}
	},

	data() {
		return {
			hoveredLessonPlanId: null as number | null,
			openCard: null as string | null,
		};
	},

	computed: {
		...mapStores(useOrganisationsStore),

		goalGroups(): CurriculumGoalGroup[] {
			// Same grouping + ordering as GoalsField/GoalsDisplay; within a group a
			// read-only list reads best sorted by path.
			return Curriculum.groupGoals(
				(this.lessonPlan.goals ?? []) as Goal[],
				this.organisationsStore.currentOrganisation?.curriculum ?? null,
			).map((group) => ({
				...group,
				goals: [...group.goals].sort(
					(a, b) => (a.fullPath || '').localeCompare(b.fullPath || '')
				),
			}));
		},
	},

	methods: {

		extractText(tiptapJson: string) {
			return tiptapUtils.extractText(tiptapJson);
		},

		onLessonPlanClicked(lessonPlan: LessonPlan) {
			this.$emit('lessonPlanClicked', lessonPlan);
		},

		goalBadgeLabel(group: CurriculumGoalGroup): string {
			return this.goalGroups.length > 1
				? `${group.curriculum.name}`
				: `${countParentGoals(group.goals)}`;
		},

		sortedAttachments(lessonPlan: any) {
			return [...lessonPlan.attachments].sort((a: any, b: any) => b.created_at.localeCompare(a.created_at));
		},

		closeHoverCards() {
			this.openCard = null;
		},
	},
}
</script>
